import { apiPaths } from '../apiPaths'
import omit from 'lodash/omit'
import type { PBPNonPaginatedListResponse, PBPResponse } from '../types/pbpResponses.types'
import type { PBPListResponse } from '@percent/utility'
import type {
  VolunteeringPartnerServiceCapabilities,
  VolunteeringPartner,
  GetVolunteeringOrganisationCreatedOpportunitiesQuery,
  CausesPortalOpportunityDto,
  CreateVolunteeringEventPayload,
  OpportunityDetailDto,
  VolunteeringSkillDto,
  VolunteeringActivityDto,
  GetVolunteeringPartnerRegionsResponse,
  EditVolunteeringOpportunityPayload,
  OpportunityLocationDto
} from './volunteeringService.types'

export const volunteeringService = ({ percentClient }: VolunteeringPartnerServiceCapabilities) => ({
  getVolunteeringPartners: () =>
    percentClient.get<PBPNonPaginatedListResponse<VolunteeringPartner>>(apiPaths.v1VolunteeringPartners),

  getOrganisationCreatedOpportunities: (params: GetVolunteeringOrganisationCreatedOpportunitiesQuery) =>
    percentClient.get<PBPListResponse<CausesPortalOpportunityDto>>(
      apiPaths.v1VolunteeringOrganisationCreatedOpportunities,
      {
        params: {
          pageSize: params.pageSize,
          cursor: params.cursor
        }
      }
    ),

  getAttendeesReport: (opportunityId: string): Promise<Blob> =>
    percentClient
      .get(
        apiPaths.v1VolunteeringOrganisationCreatedOpportunitiesParticipants.replace('{opportunityId}', opportunityId),
        {
          responseType: 'blob'
        }
      )
      .then(response => response.data),

  cancelOpportunity: (opportunityId: string) =>
    percentClient.post(apiPaths.v1CancelVolunteeringOpportunity.replace('{opportunityId}', opportunityId)),

  createOpportunity: async (payload: CreateVolunteeringEventPayload) => {
    const serialized = {
      type: payload.type,
      name: payload.name,
      description: payload.description,
      partnerId: payload.partnerId,
      location: JSON.stringify(payload.location),
      startDate: !payload.startDate ? null : payload.startDate.toUTCString(),
      endDate: !payload.endDate ? null : payload.endDate.toUTCString(),
      participantSpots: payload.participantSpots,
      timeTracking: payload.timeTracking,
      notificationsEnabled: payload.notificationsEnabled,
      skills: payload.skills.length ? JSON.stringify(payload.skills) : '[]',
      activities: payload.activities.length ? JSON.stringify(payload.activities) : '[]',
      image: payload.image,
      targetCountries: payload.targetCountries?.length ? JSON.stringify(payload.targetCountries) : undefined
    }

    const data = Object.entries(serialized).reduce<FormData>((acc, [key, value]) => {
      if (typeof value === 'string') {
        if (value.length) acc.append(key, value)
      } else if (typeof value === 'number' || typeof value === 'boolean') acc.append(key, value.toString())
      else if (value !== undefined && value !== null) acc.append(key, value)

      return acc
    }, new FormData())

    const requestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    }

    const response = await percentClient.post<PBPResponse<OpportunityDetailDto>>(
      apiPaths.v1VolunteeringOrganisationCreatedOpportunities,
      data,
      requestConfig
    )

    return response.data
  },

  editVolunteeringOpportunityLocation: async (opportunityId: string, payload: OpportunityLocationDto) => {
    const response = await percentClient.put<PBPResponse<OpportunityDetailDto>>(
      apiPaths.v1EditVolunteeringOrganisationCreatedOpportunityLocation.replace('{opportunityId}', opportunityId),
      payload,
      {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      }
    )

    return response.data
  },

  editVolunteeringOpportunity: async (opportunityId: string, payload: EditVolunteeringOpportunityPayload) => {
    const serialized = {
      name: payload.name,
      description: payload.description,
      location: payload.location ? JSON.stringify(omit(payload.location, ['type'])) : undefined,
      startDate: payload.startDate?.toISOString(),
      endDate: payload.endDate?.toISOString(),
      participantSpots: typeof payload.participantSpots === 'number' ? payload.participantSpots : 'unlimited',
      notificationsEnabled: payload.notificationsEnabled,
      skills: payload.skills?.length ? JSON.stringify(payload.skills) : undefined,
      activities: payload.activities?.length ? JSON.stringify(payload.activities) : undefined,
      image: payload.image,
      targetCountries: payload.targetCountries?.length ? JSON.stringify(payload.targetCountries) : undefined
    }

    const data = Object.entries(serialized).reduce<FormData>((acc, [key, value]) => {
      if (typeof value === 'string' && value.length) acc.append(key, value)
      else if (typeof value === 'number' || typeof value === 'boolean') acc.append(key, value.toString())
      else if (typeof value !== 'string' && value !== undefined && value !== null) acc.append(key, value)

      return acc
    }, new FormData())

    const requestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    }

    const response = await percentClient.patch<PBPResponse<OpportunityDetailDto>>(
      apiPaths.v1EditVolunteeringOrganisationCreatedOpportunity.replace('{opportunityId}', opportunityId),
      data,
      requestConfig
    )

    return response.data
  },

  getVolunteeringOpportunityDetails: (opportunityId: string) =>
    percentClient.get<PBPResponse<OpportunityDetailDto>>(
      apiPaths.v1VolunteeringOpportunityDetails.replace('{id}', opportunityId)
    ),

  getVolunteeringPartnerSkills: ({ partnerId }: { partnerId: string }) =>
    percentClient.get<PBPNonPaginatedListResponse<VolunteeringSkillDto>>(
      apiPaths.v1VolunteeringPartnerSkills.replace('{partnerId}', partnerId)
    ),

  getVolunteeringPartnerActivities: ({ partnerId }: { partnerId: string }) =>
    percentClient.get<PBPNonPaginatedListResponse<VolunteeringActivityDto>>(
      apiPaths.v1VolunteeringPartnerActivities.replace('{partnerId}', partnerId)
    ),

  getVolunteeringPartnerRegions: ({ partnerId }: { partnerId: string }) =>
    percentClient.get<GetVolunteeringPartnerRegionsResponse>(
      apiPaths.v1VolunteeringPartnerRegions.replace('{partnerId}', partnerId)
    )
})
